import { useRef, type ReactNode } from 'react';
import {
	AlertDialog,
	AlertDialogOverlay,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogBody,
	AlertDialogFooter,
} from '@chakra-ui/react';
import { Button } from './Button';

/**
 * ConfirmDialog — the single confirmation pattern (replaces ad-hoc inline
 * AlertDialogs / "are you sure" modals). Focuses the cancel button by default
 * for safe keyboard dismissal.
 */
export interface ConfirmDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	title: ReactNode;
	children?: ReactNode;
	confirmLabel?: string;
	cancelLabel?: string;
	/** Color scheme of the confirm button. Use 'red' for destructive actions. */
	confirmColorScheme?: string;
	isConfirmLoading?: boolean;
}

export const ConfirmDialog = ({
	isOpen,
	onClose,
	onConfirm,
	title,
	children,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	confirmColorScheme = 'brand',
	isConfirmLoading,
}: ConfirmDialogProps) => {
	const cancelRef = useRef<HTMLButtonElement>(null);
	return (
		<AlertDialog isOpen={isOpen} onClose={onClose} leastDestructiveRef={cancelRef} isCentered>
			<AlertDialogOverlay>
				<AlertDialogContent borderRadius="2xl">
					<AlertDialogHeader fontSize="lg" fontWeight="semibold">
						{title}
					</AlertDialogHeader>
					{children ? <AlertDialogBody color="fg.muted">{children}</AlertDialogBody> : null}
					<AlertDialogFooter gap={3}>
						<Button ref={cancelRef} variant="ghost" colorScheme="gray" onClick={onClose}>
							{cancelLabel}
						</Button>
						<Button
							colorScheme={confirmColorScheme}
							onClick={onConfirm}
							isLoading={isConfirmLoading}
						>
							{confirmLabel}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialogOverlay>
		</AlertDialog>
	);
};

export default ConfirmDialog;
